/* eslint-disable */
import {Injectable} from '@angular/core';
import {Observable, Observer} from 'rxjs';
import {ObservableErrorResourceParser} from '../../util/errors';
import {FieldValidationError, QueryResult, ResourceQueryResult} from '../../util/services';
import {List, Map as ImmutableMap} from 'immutable';
import {EmptyMessage, IdentityMessage} from '../../util/messages';
import {FilterField} from '../../query/filterfields';
import {OrderField} from '../../query/orderfields';
import {
  TaskCustomStatisticsResource,
  TaskCustomStatisticsResourceService
} from './task-custom-statistics-resource.service';
import {CustomStatistics} from "../../custom-statistics/custom-statistics";
import {CustomStatisticsResource} from "../../custom-statistics/custom-statistics-resource";
import {Form} from "../../form/form.service";


/* eslint-enable */

@Injectable()
export class TaskCustomStatisticsService implements TaskCustomStatistics.Service {

  private readonly filterField = new FilterField.CustomStatistics();
  private readonly orderField = new OrderField.CustomStatistics();
  private readonly fields = new CustomStatistics.Fields.CustomStatistics();
  private customStatMapper;

  constructor(private resourceService: TaskCustomStatisticsResourceService) {
    this.customStatMapper = new CustomStatistics.Mapper(new Form.ResourceMapper());
  }

  query(request: TaskCustomStatistics.QueryRequest): Observable<QueryResult<CustomStatistics.CustomStatistics>> {
    return Observable.create((observer: Observer<QueryResult<CustomStatistics.CustomStatistics>>) => {
      const resourceRequest = TaskCustomStatistics.toResourceQueryRequest(this.filterField, this.orderField, this.fields, this.customStatMapper, request);
      return this.resourceService.query(resourceRequest).subscribe(
        (result: ResourceQueryResult<CustomStatisticsResource.CustomStatistics>) => {
          observer.next({
            items: List.of(...result.items.map((item) => TaskCustomStatistics.toPublic(this.customStatMapper, item))),
            pagingResult: result.pagingResult
          });
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  get(request: TaskCustomStatistics.GetRequest): Observable<CustomStatistics.CustomStatistics> {
    return Observable.create((observer: Observer<CustomStatistics.CustomStatistics>) => {
      const resourceRequest = TaskCustomStatistics.toResourceGetRequest(this.fields, this.customStatMapper, request);
      return this.resourceService.get(resourceRequest).subscribe(
        (result: CustomStatisticsResource.CustomStatistics) => {
          observer.next(TaskCustomStatistics.toPublic(this.customStatMapper, result));
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  getResults(request: TaskCustomStatistics.GetResultRequest): Observable<CustomStatistics.CustomStatisticsResult> {
    return Observable.create((observer: Observer<CustomStatistics.CustomStatisticsResult>) => {
      const resourceRequest: TaskCustomStatisticsResource.GetResultRequest = {
        id: request.id,
        task_id: request.taskId,
        dql: request.dql
      };
      return this.resourceService.getResults(resourceRequest).subscribe(
        (result: CustomStatisticsResource.CustomStatisticsResult) => {
          observer.next(TaskCustomStatistics.toPublicResult(this.customStatMapper, result));
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  create(request: TaskCustomStatistics.CreateRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest = TaskCustomStatistics.toResourceCreateRequest(this.customStatMapper, request);
      return this.resourceService.create(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: any) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  update(request: TaskCustomStatistics.UpdateRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest = TaskCustomStatistics.toResourceUpdateRequest(this.customStatMapper, request);
      return this.resourceService.update(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: any) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  delete(request: TaskCustomStatistics.GetRequest): Observable<EmptyMessage> {
    return Observable.create((observer: Observer<EmptyMessage>) => {
      const resourceRequest = TaskCustomStatistics.toResourceGetRequest(this.fields, this.customStatMapper, request);
      return this.resourceService.delete(resourceRequest).subscribe(
        (result: EmptyMessage) => {
          observer.next(result);
        },
        (error: any) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  private translateError(error: any): any {
    const res = ObservableErrorResourceParser.parseError(error);
    const fieldErrors = ObservableErrorResourceParser.extractFieldErrors(res);
    const fieldErrorMap = ObservableErrorResourceParser.toFieldErrorMap(Keys.toValidatedField, fieldErrors);
    if (!fieldErrorMap.isEmpty()) {
      return FieldValidationError.of(fieldErrorMap);
    }
    return error;
  }
}

export namespace TaskCustomStatistics {


  export interface Service {

    // <editor-fold desc="CRUD">

    query(request: QueryRequest): Observable<QueryResult<CustomStatistics.CustomStatistics>>;

    get(request: GetRequest): Observable<CustomStatistics.CustomStatistics>;

    getResults(request: GetResultRequest): Observable<CustomStatistics.CustomStatisticsResult>;

    create(request: CreateRequest): Observable<IdentityMessage>;

    update(request: UpdateRequest): Observable<IdentityMessage>;

    delete(request: GetRequest): Observable<EmptyMessage>;

    // </editor-fold>

  }

  // <editor-fold desc="CRUD">

  export interface QueryRequest extends CustomStatistics.QueryRequest {
    taskId: number;
  }

  export interface GetRequest extends CustomStatistics.GetRequest {
    taskId: number;
  }

  export interface GetResultRequest extends CustomStatistics.GetResultRequest {
    taskId: number;
  }

  export interface CreateRequest extends CustomStatistics.CustomStatisticsCreateRequest {
    taskId: number;
  }

  export interface UpdateRequest extends CustomStatistics.CustomStatisticsUpdateRequest {
    taskId: number;
    id: number;
  }

  export function toResourceQueryRequest(
    filterField: FilterField.CustomStatistics,
    orderField: OrderField.CustomStatistics,
    fields: CustomStatistics.Fields.CustomStatistics,
    mapper: CustomStatistics.Mapper,
    request: QueryRequest): TaskCustomStatisticsResource.QueryRequest {
    const baseRequest: CustomStatisticsResource.QueryRequest = mapper.toResourceQueryRequest(filterField, orderField, fields, request);
    return {...baseRequest, task_id: request.taskId};
  }

  export function toResourceGetRequest(
    fields: CustomStatistics.Fields.CustomStatistics,
    mapper: CustomStatistics.Mapper,
    request: GetRequest): TaskCustomStatisticsResource.GetRequest {
    const baseRequest: CustomStatisticsResource.GetRequest = mapper.toResourceGetRequest(fields, request);
    return {...baseRequest, task_id: request.taskId};
  }

  export function toResourceCreateRequest(mapper: CustomStatistics.Mapper, request: CreateRequest): TaskCustomStatisticsResource.TaskCustomStatisticsCreateRequest {
    const baseRequest = mapper.fromPublicStatisticsCreate(request);
    return {...baseRequest, task_id: request.taskId};
  }

  export function toResourceUpdateRequest(mapper: CustomStatistics.Mapper, request: UpdateRequest): TaskCustomStatisticsResource.TaskCustomStatisticsUpdateRequest {
    const baseRequest = mapper.fromPublicStatisticsUpdate(request);
    return {...baseRequest, task_id: request.taskId};
  }


  export function toPublic(mapper: CustomStatistics.Mapper, r: CustomStatisticsResource.CustomStatistics): CustomStatistics.CustomStatistics {
    const baseResource = mapper.toPublicStatistics(r);
    return baseResource;
  }

  export function toPublicResult(mapper: CustomStatistics.Mapper, result: CustomStatisticsResource.CustomStatisticsResult) {
    return mapper.toPublicResult(result);
  }


  export enum ValidatedField {
    UNKNOWN,
    NAME,
    EXTERNAL_ID,
    DQL_FILTER,
  }

}

// <editor-fold desc="Internal">


class Keys {

  private static readonly NAME = 'name';
  private static readonly EXTERNAL_ID = 'external_id';
  private static readonly DQL_FILTER = 'dql_filter';

  private static readonly keyValidatedFieldMap: ImmutableMap<string, TaskCustomStatistics.ValidatedField> = ImmutableMap.of(
    Keys.NAME, TaskCustomStatistics.ValidatedField.NAME,
    Keys.EXTERNAL_ID, TaskCustomStatistics.ValidatedField.EXTERNAL_ID,
    Keys.DQL_FILTER, TaskCustomStatistics.ValidatedField.DQL_FILTER,
  );

  public static toValidatedField(fieldKey: string): TaskCustomStatistics.ValidatedField {
    return Keys.keyValidatedFieldMap.get(fieldKey, TaskCustomStatistics.ValidatedField.UNKNOWN);
  }

}

// </editor-fold>


